/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, Typography, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import { useMemo } from "react";
import {
  MaterialGroupingName,
  MaterialGroupName,
} from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { BarCell } from "../BarCell";
import { defaultTableSx } from "../tableFormatting";
import { GroupedInventorySeries } from "../types";
import { sorted } from "helpers";

export interface InventoryRow {
  id: number;
  materialGroup: string;
  inventory_mass: number;
  inventory_fraction: number;
  specific_price: number | null;
}

interface InventoryTableProps {
  inventorySeries: GroupedInventorySeries;
  selectedDate: Date | null;
  selectedMaterialGrouping: MaterialGroupingName | null;
}

export const InventoryTable = ({
  inventorySeries,
  selectedDate,
  selectedMaterialGrouping,
}: InventoryTableProps) => {
  const { t } = useTenantTranslation();

  const materialGroups = useMemo(
    () =>
      inventorySeries.availableMaterialGroupings[
        selectedMaterialGrouping ?? -1
      ] ?? [],
    [inventorySeries.availableMaterialGroupings, selectedMaterialGrouping]
  );

  const rows = useMemo(
    () =>
      buildRows(
        inventorySeries,
        selectedDate,
        selectedMaterialGrouping,
        materialGroups
      ),
    [inventorySeries, selectedDate, selectedMaterialGrouping, materialGroups]
  );

  const totalsRow = useMemo(() => buildTotalsRow(rows, t), [rows, t]);

  const columns = useBuildColumns(selectedMaterialGrouping, rows, totalsRow, t);

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 200,
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        sx={defaultTableSx}
        rows={rows}
        pinnedRows={{
          bottom: [totalsRow],
        }}
        columns={columns}
      />
    </div>
  );
};

const buildRows = (
  inventorySeries: GroupedInventorySeries,
  selectedDate: Date | null,
  selectedMaterialGrouping: MaterialGroupingName | null,
  materialGroups: MaterialGroupName[]
): InventoryRow[] => {
  const rows: InventoryRow[] = [];

  const filteredOrderedSeries = sorted(
    inventorySeries.values.filter(
      ([key]) => key.materialGroupingName === selectedMaterialGrouping
    ),
    ([key]) =>
      materialGroups.findIndex(
        (materialGroupName) => materialGroupName === key.materialGroupName
      )
  );

  let rowId = 0;

  for (const [key, series] of filteredOrderedSeries) {
    for (const [index, dateString] of series.date.entries()) {
      const date = new Date(dateString);
      if (!selectedDate || date.toISOString() === selectedDate.toISOString()) {
        const inventoryMass = series.inventory_mass[index];
        const inventoryFraction = series.inventory_fraction[index];

        if (inventoryMass && inventoryFraction) {
          rows.push({
            id: rowId,
            materialGroup: key.materialGroupName,
            inventory_mass: inventoryMass,
            inventory_fraction: inventoryFraction,
            specific_price: series.specific_price[index] ?? null,
          });
          rowId++;
        }
      }
    }
  }
  return rows;
};

const buildTotalsRow = (rows: InventoryRow[], t: TFunction): InventoryRow => {
  const totalInventoryMass = rows.reduce(
    (sum, row) => sum + row.inventory_mass,
    0
  );
  const totalInventoryFraction = rows.reduce(
    (sum, row) => sum + row.inventory_fraction,
    0
  );
  return {
    id: rows.length,
    materialGroup: t("Total"),
    inventory_mass: totalInventoryMass,
    inventory_fraction: totalInventoryFraction,
    specific_price: massWeightedSpecificPrice(rows),
  };
};

const massWeightedSpecificPrice = (rows: InventoryRow[]): number | null => {
  const { pricedMass, pricedCost } = rows.reduce(
    (totals, { inventory_mass, specific_price }) =>
      specific_price === null
        ? totals
        : {
            pricedMass: totals.pricedMass + inventory_mass,
            pricedCost: totals.pricedCost + inventory_mass * specific_price,
          },
    { pricedMass: 0, pricedCost: 0 }
  );
  return pricedMass > 0 ? pricedCost / pricedMass : null;
};

const useBuildColumns = (
  selectedMaterialGrouping: MaterialGroupingName | null,
  rows: InventoryRow[],
  totalsRow: InventoryRow,
  t: TFunction
): GridColDef[] => {
  const units = useUnitsFormatter(true);
  const theme = useTheme();

  const formatNumber = useFormatNumber();

  const hasAnyPrice = rows.some((row) => row.specific_price !== null);

  const columns: GridColDef[] = [
    {
      field: "materialGroup",
      headerName: t(selectedMaterialGrouping ?? ""),
      flex: 6,
      valueFormatter: (value?: string) => {
        const translated = !value ? "" : t(value);
        return translated;
      },
    },
    {
      field: "inventory_mass",
      type: "number",
      headerName: `${t("inventory")}${units("mass")}`,
      flex: 6,
      align: "right",
      renderCell: (params: GridRenderCellParams<InventoryRow, number>) =>
        params.row.id === rows.length ? (
          <Typography variant="body1" component="div">
            {formatNumber(0, true)(totalsRow.inventory_mass)}
          </Typography>
        ) : (
          <BarCell
            value={params.value!}
            formattedValue={formatNumber(0, true)(params.value)}
            maxValue={totalsRow.inventory_mass}
            color={theme.palette.data.blue}
          />
        ),
    },
    {
      field: "inventory_fraction",
      type: "number",
      headerName: `${t("inventory")} (%)`,
      flex: 4,
      align: "right",
      valueFormatter: (value?: number) =>
        !value ? "" : `${formatNumber(1, true)(value * 100)} %`,
    },
    ...(hasAnyPrice
      ? [
          {
            field: "specific_price",
            type: "number" as const,
            headerName: `${t("cost")}${units("specific_cost")}`,
            flex: 3,
            align: "right" as const,
            valueFormatter: (value?: number | null) =>
              value === null || value === undefined
                ? ""
                : formatNumber(2, true)(value),
          },
        ]
      : []),
  ];

  return columns;
};
